import supabase from "../config/supabase";

// users stores names in two columns; screens want one label.
const withDisplayName = (person) => {
  if (!person) return null;
  const name = `${person.first_name || ""} ${person.last_name || ""}`.trim();
  return { ...person, name: name || null };
};

/**
 * API client for managing review reports (flagging system)
 */
export const reviewReportsApi = {
  /**
   * Report/flag a review
   * @param {Object} body - Report details
   * @param {string} body.review_id - UUID of the review being reported
   * @param {string} body.reason - Reason for report ('spam', 'harassment', 'inappropriate', 'offensive', 'fake', 'other')
   * @param {string} [body.details] - Optional additional details about the report
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  async report(body) {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return { success: false, error: "Not authenticated" };

    const row = {
      review_id: body.review_id,
      reporter_id: user.id,
      reason: body.reason,
      details: body.details || null,
      status: "pending",
    };

    const { data, error } = await supabase
      .from("review_reports")
      .insert(row)
      .select()
      .single();

    if (error) {
      // Check for duplicate report
      if (error.code === "23505") {
        return { success: false, error: "You have already reported this review" };
      }
      return { success: false, error: error.message };
    }
    return { success: true, data };
  },

  /**
   * Check if current user has already reported a review
   * @param {string} reviewId - UUID of the review
   * @returns {Promise<{success: boolean, data?: boolean, error?: string}>}
   */
  async hasReported(reviewId) {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return { success: false, error: "Not authenticated" };

    const { data, error } = await supabase
      .from("review_reports")
      .select("id")
      .eq("review_id", reviewId)
      .eq("reporter_id", user.id)
      .maybeSingle();

    if (error) return { success: false, error: error.message };
    return { success: true, data: !!data };
  },

  /**
   * List review reports (admin only)
   * @param {Object} filters - Optional filters
   * @param {string} [filters.status] - Filter by status
   * @param {string} [filters.review_id] - Filter by review ID
   * @param {number} [filters.limit=50] - Number of reports to return
   * @param {number} [filters.offset=0] - Offset for pagination
   * @returns {Promise<{success: boolean, data?: {items: Array, total: number}, error?: string}>}
   */
  async list(filters = {}) {
    let query = supabase
      .from("review_reports")
      .select(
        `
        *,
        review:reviews(id, rating, comment, author_id, subject_type, subject_id, status, is_public, created_at),
        reporter:users!review_reports_reporter_id_fkey(id, first_name, last_name),
        reviewer:users!review_reports_reviewed_by_fkey(id, first_name, last_name)
      `,
        { count: "exact" }
      )
      .order("created_at", { ascending: false });

    if (filters.status) {
      query = query.eq("status", filters.status);
    }

    if (filters.review_id) {
      query = query.eq("review_id", filters.review_id);
    }

    const limit = filters.limit || 50;
    const offset = filters.offset || 0;
    query = query.range(offset, offset + limit - 1);

    const { data, error, count } = await query;

    if (error) return { success: false, error: error.message };
    return {
      success: true,
      data: {
        items: (data || []).map((report) => ({
          ...report,
          reporter: withDisplayName(report.reporter),
          reviewer: withDisplayName(report.reviewer),
        })),
        total: count || 0,
      },
    };
  },

  /**
   * Get count of pending reports (admin only)
   * @returns {Promise<{success: boolean, data?: number, error?: string}>}
   */
  async getPendingCount() {
    const { count, error } = await supabase
      .from("review_reports")
      .select("id", { count: "exact", head: true })
      .eq("status", "pending");

    if (error) return { success: false, error: error.message };
    return { success: true, data: count || 0 };
  },

  /**
   * Update a report status (admin only)
   * @param {string} reportId - UUID of the report
   * @param {Object} updates - Fields to update
   * @param {string} [updates.status] - New status
   * @param {string} [updates.admin_notes] - Admin notes
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  async update(reportId, updates) {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return { success: false, error: "Not authenticated" };

    const row = {
      ...updates,
      reviewed_by: user.id,
      reviewed_at: new Date().toISOString(),
    };

    const { data, error } = await supabase
      .from("review_reports")
      .update(row)
      .eq("id", reportId)
      .select()
      .single();

    if (error) return { success: false, error: error.message };
    return { success: true, data };
  },

  /**
   * Delete a report (admin only)
   * @param {string} reportId - UUID of the report
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  async delete(reportId) {
    const { error } = await supabase
      .from("review_reports")
      .delete()
      .eq("id", reportId);

    if (error) return { success: false, error: error.message };
    return { success: true };
  },
};
